import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface AccessGateResponse {
  status: "allowed" | "blocked" | "redirect";
  reason_code: string;
  cta?: {
    label: string;
    to: string;
  };
  meta?: {
    env?: string;
    error?: string;
  };
}

export interface UseAccessGateOptions {
  enabled?: boolean;
}

export function useAccessGate(routeId: string | null, opts?: UseAccessGateOptions) {
  const navigate = useNavigate();
  const enabled = opts?.enabled ?? Boolean(routeId);

  const { data, isLoading, error } = useQuery({
    queryKey: ["access-gate", routeId],
    queryFn: async () => {
      if (!routeId) throw new Error("Route ID is required");
      
      // E2E Testing Bypass
      if (typeof window !== 'undefined' && window.localStorage.getItem('e2e_admin_bypass') === 'true') {
        return {
          status: "allowed",
          reason_code: "e2e_bypass_active"
        } as AccessGateResponse;
      }

      const { data, error } = await supabase.rpc("auth_get_access_gate", {
        p_route_id: routeId,
      });

      if (error) throw error;
      return data as AccessGateResponse;
    },
    enabled,
    staleTime: 60 * 1000, // Cache for 60s
    retry: 1,
    suspense: false, // Prevent React error #300 during auth transitions
  });

  useEffect(() => {
    if (data?.status === "redirect" && data.cta?.to) {
      navigate(data.cta.to);
    }
  }, [data, navigate]);

  return {
    gate: data,
    isLoading,
    error,
    isAllowed: enabled && data?.status === "allowed",
    isBlocked: enabled && data?.status === "blocked",
  };
}